/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */
import React, { useEffect, useState, useRef, useContext } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
// import './mapbox-gl.css'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { MetaInfos } from 'components/metaInfos'
import useSize from 'hooks/useSize'
import { config } from './config'
import { HazardLevels } from 'components/hazardLevels'
import { PerimeterLevels } from 'components/perimeterLevels'
import { text as text_de, locales as locales_de } from 'components/locales_de'
import { text as text_fr, locales as locales_fr } from 'components/locales_fr'
import { LevelMeaning } from './levelMeaning'
import { SourceTable } from 'components/sourceTable'
import { NominatimSearch } from 'components/nominatimSearch'
import { Hint } from 'components/Hint'
import { SectionHead } from 'components/SectionHead'
import { levelNames, hazardNames, perimeterNames, languageType } from './types'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import centroid from 'turf-centroid'
window.URL.createObjectURL = function() {
  return ''
}

import mapboxgl, { MapLayerMouseEvent, MapMouseEvent } from 'mapbox-gl'
import {
  MapContext,
  MapboxMap,
  Terrain,
  AttributionControl,
  ScaleControl,
  GeolocateControl,
  NavigationControls,
  Source,
  Layer,
  ActivationButton,
  PitchControl,
  ViewportDisplay,
  Filter
} from 'components/MapboxMap'
import { HazardLayerSwitch } from 'components/HazardLayerSwitch'
const sourceData_de = require('../tamedia_hazard_sources_de.json')
const sourceData_fr = require('../tamedia_hazard_sources_fr.json')
const article_id_de = '928275021358'
const article_id_fr = '916068879717'
const teaser_DE: string[] = ['169965351181']
const teaser_FR: string[] = []
//
const periLayers = [
  'peri_rockfall_partial',
  'peri_rockfall_not',
  'peri_landslide_partial',
  'peri_landslide_not',
  'peri_water_partial',
  'peri_water_not',
  'peri_avalanche_partial',
  'peri_avalanche_not'
]
const maskGeojson = require('./ch_mask.json')
const cantonsGeojson = require('./ch_cantons.json')
//
//
let language: languageType = 'de' as languageType
//
//
const SOURCEDATA = language === 'de' ? sourceData_de : sourceData_fr
const TEXT = language === 'de' ? text_de : text_fr
const LOCALES = language === 'de' ? locales_de : locales_fr
const ARTICLE_ID = language === 'de' ? article_id_de : article_id_fr
const ARTICLETEASER = language === 'de' ? teaser_DE : teaser_FR

export function AppComponent() {
  //
  /* State */
  //
  const [container, size] = useSize()
  const [map, registerMap] = useState<mapboxgl.Map | undefined>(undefined)
  const [hazardLayers, setHazardLayers] = useState<string[] | undefined>(
    undefined
  )
  useEffect(() => {
    if (!map) return
    map.on('style.load', () => {
      setHazardLayers(
        map
          .getStyle()
          .layers?.filter(f => {
            if (
              f.id.includes('_landslide') ||
              f.id.includes('_avalanche') ||
              f.id.includes('_rockfall') ||
              f.id.includes('_water')
            )
              return f
          })
          .map(m => {
            return m.id
          })
      )
    })
  }, [map])
  return (
    <div className='index' ref={container}>
      <ThemeSwitch />
      <Masthead articleId={ARTICLE_ID} />
      <header className='ui vertical segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>{TEXT.head.title}</h1>
            <h2>{TEXT.head.lead}</h2>
          </div>
        </div>
      </header>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container'>
          <MetaInfos
            authors={[
              'Patrick Meier',
              'Duc-Quang Nguyen',
              'Dominik Balmer',
              'Dominique Botti',
              'Sylvain Besson',
              'Oliver Zihlmann',
              'Sebastian Broschinski'
            ]}
            updated={'26. Juli 2020'}
            language={language}
          />
          <div className='ta TEXT'>{TEXT.main}</div>{' '}
          <Hint big>{TEXT.disclaimer}</Hint>
          <SectionHead>{LOCALES.subsections.key}</SectionHead>
          <HazardLevels locales={LOCALES} />
          <PerimeterLevels locales={LOCALES} />
        </div>
      </div>
      <div className='ui text container nominatim'>
        <SectionHead>{LOCALES.subsections.search}</SectionHead>

        {map && (
          <NominatimSearch
            map={map}
            locales={LOCALES.nominatim}
            language={language}
          />
        )}
      </div>
      <div
        className={'mapContainer ' + (map ? (map.active ? 'active' : '') : '')}
      >
        {' '}
        <MapboxMap
          getMap={(map: mapboxgl.Map) => {
            registerMap(map)
            // Create marker for user position
            var el = document.createElement('div')
            el.className = 'marker'
            el.style.backgroundImage =
              'url(' + require('images/marker.svg') + ')'
            map.marker = new mapboxgl.Marker(el)
              .setLngLat([0, 0])
              .addTo((map as unknown) as mapboxgl.Map)
          }}
          token='pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'
          initOptions={{
            style: 'mapbox://styles/ta-interaktiv/ckki7s03x0m3x17tiljtrs7gc',
            zoom: size.isMobile ? 6.5 : 7,
            maxZoom: 17,
            minZoom: size.isMobile ? 6.5 : 7,
            center: [8, 46.8],
            maxBounds: [
              [4.5, 45.3],
              [12, 47.9]
            ],
            attributionControl: false
          }}
        >
          <>
            <Terrain
              options={{
                source: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                exaggeration: [
                  'interpolate',
                  ['exponential', 0.5],
                  ['zoom'],
                  0,
                  2,
                  7,
                  1.4
                ]
              }}
            />

            {/* CONTROLS */}
            <AttributionControl
              options={{ customAttribution: LOCALES.attribution }}
            />
            {size.isDesktop && <ScaleControl position='bottom-left' />}
            <NavigationControls />
            <PitchControl />
            <GeolocateControl />
            <ActivationButton
              locales={size.isMobile ? LOCALES.map.mobile : LOCALES.map.desktop}
            />
            {/* <ViewportDisplay /> */}
            {/* SOURCES */}
            <Source
              sourceId='maskGeojson'
              options={{
                type: 'geojson',
                data: maskGeojson
              }}
            />
            <Source
              sourceId='cantonsGeojson'
              options={{
                type: 'geojson',
                data: cantonsGeojson
              }}
            />
            {/* LAYER */}
            <Layer
              key={'a'}
              add
              options={{
                id: 'maskGeojson',
                type: 'fill',
                source: 'maskGeojson',
                paint: {
                  'fill-color': '#fff',
                  'fill-opacity': 1
                }
              }}
              below='hillshade_low'
            />
            <Layer
              key={'b'}
              add
              options={{
                id: 'cantonsGeojson',
                type: 'fill',
                source: 'cantonsGeojson',
                paint: {
                  'fill-color': {
                    stops: [
                      [9, 'rgba(255, 255, 255, 0.8)'],
                      [10, 'rgba(255, 255, 255, 0.9)']
                    ]
                  },
                  'fill-outline-color': {
                    stops: [
                      [9, 'rgba(153, 153, 153, 0)'],
                      [10, '#999']
                    ]
                  }
                },
                filter: ['any', ['==', 'abbr', 'NW'], ['==', 'abbr', 'OW']]
              }}
              below='hillshade_low'
            />
            <Layer
              key={'c'}
              add
              options={{
                id: 'nodatacantons-label',
                type: 'symbol',
                source: 'cantonsGeojson',
                minzoom: 9,
                filter: ['any', ['==', 'abbr', 'OW'], ['==', 'abbr', 'NW']],
                layout: {
                  'text-field': 'Keine Daten für {abbr}',
                  'text-size': 15,
                  'text-font': ['Lato Light Italic'],
                  'text-max-width': 5
                },
                paint: {
                  'text-color': '#000',
                  'text-opacity': 0.6
                }
              }}
              below='hillshade_low'
            />
            <Layer
              key={'d'}
              add
              options={{
                id: 'sky',
                type: 'sky',
                paint: {
                  'sky-type': 'gradient',
                  'sky-gradient': [
                    'interpolate',
                    ['linear'],
                    ['sky-radial-progress'],
                    0.8,
                    'rgba(135, 206, 235, 1.0)',
                    1,
                    'rgba(0,0,0,0.1)'
                  ],
                  'sky-gradient-center': [0, 0],
                  'sky-gradient-radius': 90,
                  'sky-opacity': [
                    'interpolate',
                    ['exponential', 0.1],
                    ['zoom'],
                    5,
                    0,
                    22,
                    1
                  ]
                }
              }}
            />
            {hazardLayers &&
              hazardLayers.map((p, i) => {
                const showtooltip = (
                  e: MapLayerMouseEvent,
                  map: mapboxgl.Map
                ) => {
                  const coordinates = e.lngLat
                  const features = map.queryRenderedFeatures(e.point)
                  const list: string[] = []
                  features.forEach(feat => {
                    if (feat.properties) {
                      let h: hazardNames = feat.properties.h
                      let l: levelNames = feat.properties.l
                      if (h && l) {
                        let el = `<div class='entry ${l}' style='background-color:${
                          config.colors.hazards[h][l]
                        }'><img  src='${require(`images/icons_${h}.svg`)}'/>${
                          LOCALES.hazards[h]
                        } - ${LOCALES.levels[l]}</div>`

                        if (list.indexOf(el) === -1) list.push(el)
                      }
                    }
                  })
                  map.getCanvas().style.cursor = 'pointer'

                  if (map.popups['HazardPopup']) {
                    map.popups['HazardPopup'].remove()
                  }
                  map.popups['HazardPopup'] = new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false
                  })
                    .setLngLat(coordinates)
                    .setHTML(list.join(''))
                    .setMaxWidth('400px')
                    .addTo(map)
                }
                return (
                  <Layer
                    key={i}
                    modify
                    id={p}
                    events={{
                      mousemove: showtooltip,
                      touchstart: showtooltip,
                      mouseleave: (e: MapLayerMouseEvent, map) => {
                        map.getCanvas().style.cursor = ''
                        map.popups['HazardPopup'].remove()
                      }
                    }}
                  />
                )
              })}

            {periLayers.map((p, i) => (
              <Layer
                key={'p' + i}
                modify
                id={p}
                events={{
                  mousemove: (e: MapLayerMouseEvent, map: mapboxgl.Map) => {
                    if (!e.features || e.features.length === 0) return
                    const feature = e.features[0]
                    map.getCanvas().style.cursor = 'pointer'

                    if (feature.properties) {
                      if (map.popups['PerimeterPopUp']) {
                        map.popups['PerimeterPopUp'].remove()
                      }
                      map.popups['PerimeterPopUp'] = new mapboxgl.Popup({
                        closeButton: false,
                        closeOnClick: false
                      })
                        .setLngLat(centroid(feature).geometry.coordinates)
                        .setHTML(
                          '<div class="entry"><b>' +
                            feature.properties.na +
                            '</b><br/>' +
                            LOCALES.perimeter.steps[
                              p.split('_')[2] as perimeterNames
                            ] +
                            '</div>'
                        )
                        .addTo(map)
                    }
                  },
                  mouseleave: (e: MapLayerMouseEvent, map) => {
                    map.getCanvas().style.cursor = ''
                    map.popups['PerimeterPopUp'].remove()
                  }
                }}
              />
            ))}
            {/* LAYER SWITCH */}
            {MapContext && (
              <HazardLayerSwitch
                isMobile={size.isMobile}
                hazards={config.allHazards.map(m => {
                  return {
                    name: LOCALES.hazards[m as hazardNames],
                    id: m,
                    color: config.colors.hazards[m].mean
                  }
                })}
              />
            )}
          </>
        </MapboxMap>
      </div>
      {/* <MapboxGLMap
        isactive={mapOptions.isActive}
        toggleActivation={setMapIsActive}
        language={language}
        locales={LOCALES}
        getMap={map => {
          var layers = map.getStyle().layers

          //
          map.addSource('maskGeojson', {
            type: 'geojson',
            data: maskGeojson
          })
          map.addLayer(
            {
              id: 'maskGeojson',
              type: 'fill',
              source: 'maskGeojson',
              layout: {},
              paint: {
                'fill-color': '#fff',
                'fill-opacity': 1
              }
            },
            'hillshade_low'
          )
          console.log('LAYERS', map.getStyle().layers)
          console.log('SOURCES', map.getStyle().sources)

          // Create marker for user position
          var el = document.createElement('div')
          el.className = 'marker'
          el.style.backgroundImage = 'url(' + require('images/marker.svg') + ')'
          map.marker = new mapboxgl.Marker(el)
            .setLngLat([0, 0])
            .addTo((map as unknown) as mapboxgl.Map)

          // Pattern für Perimeter
          map.addImageToMap(require('images/icons_pattern.png'), 'stroked')
          map.addImageToMap(
            require('images/icons_pattern_big.png'),
            'stroked_big'
          )
          map.addImageToMap(require('images/icons_pattern_2.png'), 'stroked_2')
          map.addImageToMap(
            require('images/icons_pattern_2_big.png'),
            'stroked_2_big'
          )
          // create popup
          let popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false
          })
          // layer-ids for not/not fully checked areas
          let periLayers = [
            'peri_rockfall_partial',
            'peri_rockfall_not',
            'peri_landslide_partial',
            'peri_landslide_not',
            'peri_water_partial',
            'peri_water_not',
            'peri_avalanche_partial',
            'peri_avalanche_not'
          ]
          // add popup/tooltip for not/not fully checked areas
          periLayers.forEach(peri => {
            map.on('mousemove', peri, e => {
              if (!e.features || e.features.length === 0) return
              var coordinates = centroid(e.features[0]).geometry.coordinates
              map.getCanvas().style.cursor = 'pointer'

              if (e.features[0].properties) {
                popup
                  .setLngLat(coordinates)
                  .setHTML(
                    '<div class="entry"><b>' +
                      e.features[0].properties.na +
                      '</b><br/>' +
                      LOCALES.perimeter.steps[
                        peri.split('_')[2] as perimeterNames
                      ] +
                      '</div>'
                  )
                  .addTo(map)
              }
            })
            map.on('mouseleave', peri, e => {
              map.getCanvas().style.cursor = ''

              popup.remove()
            })
          })
          // map.addSource('mapbox-dem', {
          //   type: 'raster-dem',
          //   url: 'https://api.maptiler.com/tiles/terrain-rgb/tiles.json?key=nu1rAHfpx6ICwiBr4BQr',
          //   // url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          //   tileSize: 512,
          //   maxzoom: 14
          // })
          // add the DEM source as a terrain layer with exaggerated height
          map.setTerrain({ source: 'mapbox://mapbox.mapbox-terrain-dem-v1', exaggeration:[
            'interpolate',
            ['exponential', 0.5],
            ['zoom'],
            0,
            2,
            7,
            1.6,
          ]})
          map.addLayer({
            id: 'sky',
            type: 'sky',
            paint: {
              'sky-type': 'atmosphere',
              'sky-atmosphere-sun': [0.0, 0.0],
              'sky-atmosphere-sun-intensity': 15
            }
          })
          // Get Hazard-Layer Ids
          let hazardLayers = layers
            ?.filter(
              f =>
                f.id.includes('_landslide') ||
                f.id.includes('_avalanche') ||
                f.id.includes('_rockfall') ||
                f.id.includes('_water')
            )
            ?.map(m => m.id)

          console.log('HAZARD LAYERS', hazardLayers)
          // Add Tooltip for Hazard Layers
          hazardLayers?.forEach(peri => {
            map.on('mousemove', peri, e => {
              let coordinates = e.lngLat
              var features = map.queryRenderedFeatures(e.point)
              let list: string[] = []
              features.forEach(feat => {
                if (feat.properties) {
                  let h: hazardNames = feat.properties.h
                  let l: levelNames = feat.properties.l
                  if (h && l) {
                    let el =
                      "<div class='entry " +
                      l +
                      "' style='background-color:" +
                      config.colors.hazards[h][l] +
                      "'><img  src='" +
                      require('images/icons_' + h + '.svg') +
                      "'/>" +
                      LOCALES.hazards[h] +
                      ' - ' +
                      LOCALES.levels[l] +
                      '</div>'
                    if (list.indexOf(el) === -1) list.push(el)
                  }
                }
              })
              ///
              map.getCanvas().style.cursor = 'pointer'
              popup
                .setLngLat(coordinates)
                .setHTML(list.join(''))
                .setMaxWidth('400px')

                .addTo(map)
            })
            map.on('mouseleave', peri, e => {
              map.getCanvas().style.cursor = ''

              popup.remove()
            })
          })
          // Map zum State
          registerMap(map)
        }}
      >
        <>
          <MapActivationButton
            current={mapOptions.isActive}
            toggle={setMapIsActive}
            locales={size.isMobile ? LOCALES.map.mobile : LOCALES.map.desktop}
          />
          {!hazardused && (
            <span className='tooltip bottom hazards'>
              {LOCALES.hints.hazards}
            </span>
          )}
          {!activationused && !size.isMobile && (
            <span className='tooltip top activation'>
              {LOCALES.hints.interaction}
            </span>
          )}

          <div className='hazardswitchcontainer'>
            {config.allHazards.map((h, i) => (
              <button
                key={i}
                id={h}
                onClick={toggleHazards}
                style={{
                  backgroundColor: config.colors.hazards[h].mean
                }}
                className={
                  'hazardswitch ' +
                  h +
                  ' ' +
                  (mapOptions.hiddenHazards.indexOf(h) !== -1 ? 'off' : '')
                }
              >
                <img src={require('images/icons_' + h + '.svg')} />
                {(!size.isMobile ||
                  (mapOptions.hiddenHazards.indexOf(h) === -1 &&
                    mapOptions.hiddenHazards.length === 3)) && (
                  <div>{LOCALES.hazards[h as hazardNames]}</div>
                )}
              </button>
            ))}
          </div>
        </>
      </MapboxGLMap>{' '} */}
      <>
        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta TEXT'>
              <h4 className='title'>{TEXT.levelmeaning.title}</h4>
              <div className='sub'>{TEXT.levelmeaning.sub}</div>
              {Object.keys(TEXT.levelmeaning.levels).map((l, i) => {
                return (
                  <React.Fragment key={i}>
                    <LevelMeaning level={l as levelNames} text={TEXT} />
                  </React.Fragment>
                )
              })}
              <div className='zonedefinitionwrapper'>
                <h4 className='title'>{TEXT.zonedefinition.title}</h4>
                <div className='sub'>{TEXT.zonedefinition.sub}</div>
                <div className='imageContainer'>
                  {TEXT.zonedefinition.image}
                </div>
                {TEXT.zonedefinition.points.map((p, i) => {
                  return (
                    <div className='point' key={i}>
                      <h5>{p[0]} </h5>

                      {p[1]}
                    </div>
                  )
                })}
              </div>
              <div className='contentafterwrapper'>{TEXT.contentafter}</div>
              <Hint big>{TEXT.reply}</Hint>
            </div>
          </div>
        </div>
        <Footer
          mailTo={language === 'fr' ? 'Duc-Quang.Nguyen@tamedia.ch' : undefined}
          hidePortfolioTeaser={language === 'fr' ? true : false}
          questions={LOCALES.questions}
          articleId={ARTICLE_ID}
          credits={[
            [
              LOCALES.other.credits[0],
              'Dominik Balmer, Sylvain Besson, Dominique Botti, Oliver Zihlmann'
            ],
            [LOCALES.other.credits[1], 'Patrick Meier, Duc-Quang Nguyen'],
            [LOCALES.other.credits[2], 'Sebastian Broschinski']
          ]}
          locale={language}
        >
          {ARTICLETEASER.length > 0 && (
            <div className='ui container' style={{ marginBottom: '2em' }}>
              <h4>{LOCALES.other.teaserTitle}</h4>
              <ArticleTeasers
                numberOfCards='three'
                articleIds={ARTICLETEASER}
              />
            </div>
          )}
          <div className='ui vertical grey segment methodik'>
            <div className='ui text container'>
              <div className='title'>{TEXT.technical_head}</div>
              <div className='methodikText'>{TEXT.technical}</div>
              <br />
              <SourceTable sources={SOURCEDATA} locales={LOCALES} />
            </div>
          </div>
        </Footer>
      </>
      {/* )} */}
    </div>
  )
}
